// Uploads to a knowledge base: three at once, each with its progress and Cancel; when one ends,
// the documents and generations are read again. Leaving the tab cancels what is still going.
import type { Uploaded } from "@cosmosai/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { knowledgeKeys, useUploadDocument } from "../../api/knowledge-queries";

const AT_ONCE = 3;

export interface UploadRow {
  key: string;
  name: string;
  size: number;
  /** From 0 to 1. */
  progress: number;
  state: "waiting" | "uploading" | "done" | "failed";
  outcome?: Uploaded["status"];
  error?: unknown;
}

let next = 0;

export function useUploads(workspace: string, project: string, kb: string) {
  const upload = useUploadDocument(workspace, project, kb);
  const queries = useQueryClient();
  const [rows, setRows] = useState<UploadRow[]>([]);
  const waiting = useRef<Array<{ key: string; file: File }>>([]);
  const running = useRef(new Map<string, AbortController>());

  // The rows live in this panel: once it unmounts nothing shows them, so their requests stop.
  useEffect(() => {
    const inFlight = running.current;
    return () => {
      waiting.current = [];
      for (const controller of inFlight.values()) controller.abort();
    };
  }, []);

  const patch = useCallback((key: string, change: Partial<UploadRow>) => {
    setRows((all) => all.map((row) => (row.key === key ? { ...row, ...change } : row)));
  }, []);

  const pump = useCallback(() => {
    while (running.current.size < AT_ONCE && waiting.current.length) {
      const { key, file } = waiting.current.shift() as { key: string; file: File };
      const controller = new AbortController();
      running.current.set(key, controller);
      patch(key, { state: "uploading" });
      upload({
        file,
        signal: controller.signal,
        onProgress: (progress) => patch(key, { progress }),
      })
        .then((answer) => patch(key, { state: "done", progress: 1, outcome: answer.status }))
        .catch((error: unknown) => {
          if (controller.signal.aborted) setRows((all) => all.filter((row) => row.key !== key));
          else patch(key, { state: "failed", error });
        })
        .finally(() => {
          running.current.delete(key);
          void queries.invalidateQueries({ queryKey: knowledgeKeys.kb(workspace, project, kb) });
          pump();
        });
    }
  }, [upload, kb, patch, project, queries, workspace]);

  /** Queues files; each is sent in its own request. */
  const start = (files: File[]) => {
    const added = files.map((file) => ({ key: `kb-upload-${++next}`, file }));
    setRows((all) => [
      ...all,
      ...added.map(({ key, file }) => ({
        key,
        name: file.name,
        size: file.size,
        progress: 0,
        state: "waiting" as const,
      })),
    ]);
    waiting.current.push(...added);
    pump();
  };

  const cancel = (key: string) => {
    waiting.current = waiting.current.filter((entry) => entry.key !== key);
    const controller = running.current.get(key);
    if (controller) controller.abort();
    else setRows((all) => all.filter((row) => row.key !== key));
  };

  const dismiss = (key: string) => setRows((all) => all.filter((row) => row.key !== key));

  return { rows, start, cancel, dismiss };
}
